import { useRef } from 'react';
import { noop } from 'lodash';
import trim from 'lodash/trim';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { Skeleton } from '../../ui/feedback/Skeleton';
import { Box } from '../../ui/layout/Box';
import { DataTestId } from '../../ui/layout/DataTestIdWrapper';
import { Flex } from '../../ui/layout/Flex';
import { ResizableGrid } from '../../ui/layout/ResizableGrid/ResizableGrid';
import { PostCardGrid } from '../../ui/posts/PostCardGrid/PostCardGrid';
import { POST_GRID_CARD_FOOTER_HEIGHT } from '../../ui/posts/PostCardGrid/constants';
import { calculateGridCardHeight } from '../../ui/posts/PostCardGrid/utils';
import { EventSource } from '../../utils/analyticsService';
import { useGridImageOptimizer } from '../../utils/mds/useGridImageAlias';
import { useResetScroll } from '../../utils/useResetScroll';
import { UsePostsCollectionPayload } from '../post/usePostCollection';
import { withPostCallbacks } from '../post/withPostCallbacks';
import { SearchNotFound } from './SearchNotFound';
import { buildSearchPostsScrollKey } from './searchScrollRestoration';

interface SearchPostProps {
    postCollection: UsePostsCollectionPayload;
    query: string;
}

const SKELETON_CARDS_HEIGHT = [280, 430, 340, 360, 460, 280, 430, 280];
const SKELETON_ITEMS = SKELETON_CARDS_HEIGHT.map((_, index) => ({
    id: `search-post-skeleton-${index}`,
}));

const SearchPostCardSkeleton = ({ height }: { height: number }) => (
    <>
        <Skeleton
            variant="rect"
            width="100%"
            height={height - POST_GRID_CARD_FOOTER_HEIGHT}
            borderRadius={2}
        />
        <Flex direction="row" spacing={1} py={1} px={0.5}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={100} height={16} my={0.5} />
        </Flex>
    </>
);

const EnchancedPostGridCardCompact = withPostCallbacks(PostCardGrid, EventSource.SEARCH);

export const SearchPost = (props: SearchPostProps) => {
    const { postCollection, query } = props;
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const normalizedQuery = trim(query);
    const showSkeleton =
        postCollection.items.length === 0 && (!postCollection.isLoaded || postCollection.isLoading);
    const showGrid = showSkeleton || postCollection.items.length > 0;
    const isScrollReady =
        trim(postCollection.filters?.query ?? '') === normalizedQuery &&
        postCollection.items.length > 0;
    const { getAlias: getGridImageAliasForCard } = useGridImageOptimizer();

    const { onScroll } = useResetScroll(
        gridContainerRef,
        buildSearchPostsScrollKey(normalizedQuery),
        isScrollReady,
    );

    const getItemHeight = (index: number, width: number) => {
        const postModel = postCollection.items[index];
        if (!postModel) {
            return 0;
        }

        return calculateGridCardHeight(width, toPostUI(postModel).content, postModel.postType);
    };

    const renderItem = (
        index: number,
        columns: number,
        isVisible?: boolean,
        cardSize?: { width: number; height: number },
    ) => {
        const postModel = postCollection.items[index];

        if (!postModel) {
            return null;
        }

        const imageAlias = getGridImageAliasForCard(cardSize?.width);

        return (
            <EnchancedPostGridCardCompact
                post={postModel}
                index={index}
                gridColumns={columns}
                isVisible={isVisible}
                imageAlias={imageAlias}
                hasLogPostViewEvent
            />
        );
    };

    return showGrid ? (
        <Box
            height="100%"
            overflowY="auto"
            ref={gridContainerRef}
            onScroll={onScroll}
            scrollbarGutter="stable"
            dataTestId={DataTestId.SEARCH_POSTS_SCROLL_CONTAINER}
        >
            {showSkeleton ? (
                <ResizableGrid
                    key="search-post-grid-skeleton"
                    marginTop={2}
                    bottomYMargin={8}
                    containerRef={gridContainerRef}
                    items={SKELETON_ITEMS}
                    loadMore={noop}
                    getItemHeight={(index) => SKELETON_CARDS_HEIGHT[index]}
                    renderItem={(index) => (
                        <SearchPostCardSkeleton height={SKELETON_CARDS_HEIGHT[index]} />
                    )}
                />
            ) : (
                <ResizableGrid
                    key={`${postCollection.items[0]?.id}-search-post-grid`}
                    marginTop={2}
                    bottomYMargin={8}
                    containerRef={gridContainerRef}
                    items={postCollection.items}
                    loadMore={postCollection.onLoadNext}
                    getItemHeight={getItemHeight}
                    renderItem={renderItem}
                />
            )}
        </Box>
    ) : (
        <SearchNotFound />
    );
};
